/* eslint-disable camelcase */
import { useCallback } from 'react';

import _round from 'lodash/round';
import { CheckoutRequest } from 'common/types/CheckoutRequest';
import { emptyFamilyAddress } from 'common/api/crm/family/address/models/FamilyAddress';
import { useHomeDetails } from 'common/features/store/duck/home/utils/useHomeDetails';
import { HomeDetailsResponse } from 'common/api/crm/home/models/HomeDetailsResponse';
import { CreditCardTransactionTokenResponse } from 'common/api/e-comm/credit-cards/service';
import { createAPIError } from 'common/api/utils/createAPIError';
import { isDefined, noop } from 'common/utils';
import { ThreeDSResponse, ThreeDSTransactionError } from 'common/modules/3dsecure';
import {
    ConvergeCreditCardRequestBody,
    SuccessfulConvergeResponse,
    UnsuccessfulConvergeResponse
} from 'common/api/e-comm/models/Converge';

export enum ConvergeResponseType {
    Approval = 'CONVERGE_APPROVAL',
    Declined = 'CONVERGE_DECLINED',
    Error = 'CONVERGE_ERROR',
    Unknown = 'CONVERGE_UNKNOWN',
    ThreeDSecureResult = 'THREEDS_RESULT',
    ThreeDSecureError = 'THREEDS_ERROR',
    ThreeDSecureUnauthenticated = 'THREEDS_UNAUTHENTICATED',
    ThreeDSecureAuthenticated = 'THREEDS_AUTHENTICATED',
    ThreeDSecureChallengeStart = 'THREEDS_CHALLENGE_START',
    ThreeDSecureChallengeEnd = 'THREEDS_CHALLENGE_END',
}

export type ErrorMessage = { type: 'ERROR', payload: string };

export type ConvergeErrorMessage = {
    type: ConvergeResponseType.Error;
    payload: UnsuccessfulConvergeResponse;
};

export type ConvergeUnknownMessage = {
    type: ConvergeResponseType.Unknown;
    payload: unknown;
};

export type ConvergeApprovalMessage = {
    type: ConvergeResponseType.Approval;
    payload: SuccessfulConvergeResponse;
};

export type ConvergeDeclinedMessage = {
    type: ConvergeResponseType.Declined;
    payload: SuccessfulConvergeResponse | UnsuccessfulConvergeResponse;
};

export type ThreeDSecureResultMessage = {
    type: ConvergeResponseType.ThreeDSecureResult;
    payload: ThreeDSResponse | ThreeDSTransactionError;
};

export type ThreeDSecureErrorMessage = {
    type: ConvergeResponseType.ThreeDSecureError | ConvergeResponseType.ThreeDSecureUnauthenticated;
    payload: string;
};

export type ThreeDSecureAuthenticatedMessage = {
    type: ConvergeResponseType.ThreeDSecureAuthenticated;
    payload: string;
};

export type ThreeDSecureChallengeMessage = {
    type: ConvergeResponseType.ThreeDSecureChallengeStart | ConvergeResponseType.ThreeDSecureChallengeEnd;
    payload: undefined;
};

export type ThreeDSecureMessage =
    ThreeDSecureErrorMessage | ThreeDSecureChallengeMessage | ThreeDSecureAuthenticatedMessage |
    ThreeDSecureResultMessage;

export type CreditCardIncomingMessage =
    ErrorMessage | ConvergeErrorMessage | ConvergeUnknownMessage |
    ConvergeDeclinedMessage | ConvergeApprovalMessage | ThreeDSecureMessage;

export type CreditCardOutgoingMessage = {
    type: 'PAY';
    payload: {
        transactionToken3DSecure?: string;
        data: ConvergeCreditCardRequestBody;
    };
};

export interface CreateCreditCardRequestOptions {
    total: number;
    cartId: string;
    cartReferenceId: number;
    homeDetails: HomeDetailsResponse;
    request: CheckoutRequest;
    customerId: number;
    clientIpAddress: string | null;
    token: string | null;
}

const createCreditCardRequest = (options: CreateCreditCardRequestOptions): ConvergeCreditCardRequestBody => {
    const { cartId, homeDetails, request, token, total, clientIpAddress, customerId, cartReferenceId } = options;
    const { profile } = homeDetails;
    const address = request.newCreditCard.address || emptyFamilyAddress;
    const { newCreditCard } = request;

    return {
        ssl_transaction_type: 'ccsale',
        ssl_txn_auth_token: token || '',
        ssl_cvv2cvc2: newCreditCard.securityCode,
        ssl_cvv2cvc2_indicator: 1,
        ssl_add_token: newCreditCard.save ? 'Y' : 'N',
        ssl_get_token: newCreditCard.save ? 'Y' : 'N',
        ssl_vendor_id: 'sc100046',
        ssl_email: profile.email,
        ssl_avs_address: address.address1,
        ssl_city: address.city,
        ssl_state: address.state,
        ssl_avs_zip: address.zipCode.split('-')[0], // Converge throws a cryptic error if you send a zip with the +4 digits
        ssl_description: 'RaiseRight',
        ssl_first_name: profile.firstName,
        ssl_last_name: profile.lastName,
        cardholder_name: newCreditCard.name,
        ssl_card_number: newCreditCard.cardNumber,
        ssl_exp_date: newCreditCard.expirationDate.replace(/\//, ''),
        ssl_amount: _round(total, 2),
        ssl_customer_id: customerId,
        ssl_invoice_number: cartReferenceId,
        ssl_merchant_txn_id: cartId,
        ssl_cardholder_ip: clientIpAddress || '',
    };
};

export interface PayWithNewCreditCardCallbacks {
    onThreeDSecureChallengeStart: () => void;
    onThreeDSecureChallengeEnd: () => void;
    onThreeDSecureAuthenticated: (token: string) => void;
    onThreeDSecureResult: (threeDSecureResponse: ThreeDSResponse | ThreeDSTransactionError) => void;
}

export interface PayWithNewCreditCardOptions extends CreditCardTransactionTokenResponse {
    total: number;
    cartId: string;
    request: CheckoutRequest;
    callbacks: PayWithNewCreditCardCallbacks;
    transactionToken3DSecure?: string;
}

export const useCreditCard = () => {
    const homeDetails = useHomeDetails();

    const payWithNewCreditCard = useCallback(async (options: PayWithNewCreditCardOptions) => {
        if (!homeDetails) { return { error: createAPIError<null>(null, new Error('HomeDetailsResponse required')) }; }

        const { callbacks, cartId, cartReferenceId, transactionToken3DSecure, ...rest } = options;

        if (!isDefined(rest.token)) { return { error: createAPIError<null>(null, new Error('Token required')) }; }

        const payload = {
            transactionToken3DSecure,
            data: createCreditCardRequest({
                ...rest,
                homeDetails,
                cartReferenceId,
                cartId,
            }),
        };

        const {
            onThreeDSecureChallengeStart,
            onThreeDSecureChallengeEnd,
            onThreeDSecureAuthenticated,
            onThreeDSecureResult = noop,
        } = callbacks;

        // eslint-disable-next-line complexity
        return await new Promise((resolve) => {
            RR.checkout.payWithCreditCard(payload, (type) => (payload: unknown) => {
                switch (type) {
                    case ConvergeResponseType.ThreeDSecureChallengeStart:
                        onThreeDSecureChallengeStart();
                        break;
                    case ConvergeResponseType.ThreeDSecureChallengeEnd:
                        onThreeDSecureChallengeEnd();
                        break;
                    case ConvergeResponseType.ThreeDSecureAuthenticated:
                        onThreeDSecureAuthenticated(payload as string);
                        break;
                    case ConvergeResponseType.ThreeDSecureResult:
                        onThreeDSecureResult(payload as ThreeDSResponse);
                        break;
                    case ConvergeResponseType.Approval:
                    case ConvergeResponseType.Declined:
                    case ConvergeResponseType.Error:
                    case ConvergeResponseType.Unknown:
                    case ConvergeResponseType.ThreeDSecureError:
                    case ConvergeResponseType.ThreeDSecureUnauthenticated:
                        resolve({ data: { type, payload } });
                        break;
                }
            });
        });
    }, [homeDetails]);

    return {
        payWithNewCreditCard,
    };
};

